import { SubscriptionName } from '@anm/api/modules/subscription';
import findSubscription from '@anm/helpers/subscription/findSubscription';
import isTeamSubscription from '@anm/helpers/subscription/isTeamSubscription';
import useSwitchState from '@anm/hooks/useSwitchState';
import { appNotificationActions } from '@anm/store/modules/appNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { subscriptionSelectors } from '../../../../store/modules/subscription';
import { userSelectors } from '../../../../store/modules/user';
import { useTeamFetcher } from '../../../team/ManageTeam/hooks/useTeamFetch';
import UpgradeUserSubscription from '../../UpgradeUserSubscription';

import useSubscriptionChangeState from './useSubscriptionChangeState';

const useCanOpenModal = (canUpgradeCb: (canUpgrade: boolean) => void) => {
  const dispatch = useDispatch();
  const [isPending, setPending] = useState(false);
  const [maxUsers, setMaxUsers] = useState<number>();
  const [{ members, isFetchPending, isError, error }, { fetchTeamInfo }] = useTeamFetcher();
  const currentSubscription = useSelector(userSelectors.selectWaveSubscription);
  const subscriptionsList = useSelector(subscriptionSelectors.selectSubscriptionList);

  const check = (nextSubscriptionName: SubscriptionName) => {
    setPending(true);
    if (
      !currentSubscription ||
      !isTeamSubscription(nextSubscriptionName) ||
      !isTeamSubscription(currentSubscription.name) ||
      !subscriptionsList
    ) {
      setPending(false);
      canUpgradeCb(true);
      return true;
    }

    fetchTeamInfo();

    const nextTeamPlan = findSubscription(subscriptionsList)(nextSubscriptionName);
    const maxUsers = nextTeamPlan?.features.Teams?.maxUsers;

    setMaxUsers(maxUsers);
  };

  useEffect(() => {
    if (isError) {
      dispatch(
        appNotificationActions.notify({
          type: 'error',
          title: error?.message
        })
      );
      return;
    }

    if (isFetchPending || !members?.length || !maxUsers || !isPending) return;

    if (members.length > maxUsers) {
      const removeCount = members.length - maxUsers;
      dispatch(
        appNotificationActions.notify({
          type: 'error',
          title: 'Remove team members',
          description: `Please, remove ${removeCount} members of the team to downgrade subscription`
        })
      );
      canUpgradeCb(false);
    } else {
      canUpgradeCb(true);
    }
    setPending(false);
  }, [isFetchPending, maxUsers, members?.length, isError, isPending]);

  return check;
};

const useUpgradeUserSubscriptionModal = () => {
  const [isUpgradeModalOpen, openUpgradeModal, closeUpgradeModal] = useSwitchState();

  const subscriptionChange = useSubscriptionChangeState();

  const checkUpgradeAccess = useCanOpenModal(canChangePlan => canChangePlan && openUpgradeModal());
  const [subscriptionName, setSubscriptionName] = useState<SubscriptionName>();
  const handleUpgradeModalOpen = useCallback(
    (selectedSubscriptionName: SubscriptionName) => {
      setSubscriptionName(selectedSubscriptionName);
      checkUpgradeAccess(selectedSubscriptionName);
    },
    [checkUpgradeAccess]
  );

  const UpgradeUserSubscriptionModal = useMemo(
    () =>
      isUpgradeModalOpen &&
      subscriptionName && (
        <UpgradeUserSubscription subscriptionName={subscriptionName} onModalClose={closeUpgradeModal} />
      ),
    [isUpgradeModalOpen, subscriptionName]
  );

  useEffect(() => {
    !!subscriptionChange && closeUpgradeModal();
  }, [!!subscriptionChange]);

  return [UpgradeUserSubscriptionModal, handleUpgradeModalOpen, subscriptionName] as const;
};

export default useUpgradeUserSubscriptionModal;
